import { Checkbox, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  CardComponent,
  Download,
  Table,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React, { useState } from 'react';
import componentConstants from '../../../core-utils/messages/componentConstants';
import { DownloadButtons } from '../../../core-utils/helper-utils/reportsTable';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { ChevronRight } from '@mui/icons-material';
import clsx from 'clsx';
import { getTruncatedText } from '../../../pages/MemberSummaryPage';

interface RowData {
  name: string;
  description: string;
  lastUpdated: string;
}
export interface ComplianceReportsProps {
  reportsData: {
    name: string;
    description: string;
    lastUpdated: string;
    subRows?: RowData[];
  }[];
  handleDownloadAll: () => void;
  handleDownloadSelected: () => void;
  setSelectedReports: React.Dispatch<React.SetStateAction<any[]>>;
  showDownloadSelected: boolean;

  handleRowDownloadReport: (row: any) => void;
}

const useStyles = makeStyles()({
  tableCard: {
    margin: '28px 0px',
  },
  //Table styles
  tableRoot: {
    padding: '0px',
  },
  tableHeaderRow: {
    height: '48px',
    backgroundColor: patientAccessTheme.palette.grey[100],
    borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
  },
  tableTHSelection: {
    paddingLeft: '26px',
  },
  tableDataRow: {
    height: 'auto',
    minHeight: '56px',
    padding: '0px',
    borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    '& td:first-child': {
      paddingLeft: '26px',
    },
    '&:hover': {
      backgroundColor: patientAccessTheme.palette.grey[300],
    },
    display: 'flex',
    alignItems: 'center',
  },
  subRows: {
    backgroundColor: patientAccessTheme.palette.grey[100],
  },
  tableHeaderText: {
    fontWeight: 500,
    color: patientAccessTheme.palette.text.highemphasis,
    fontSize: '12px',
    lineHeight: '12px',
  },
  chevronRight: {
    height: '24px',
    width: '24px',
    color: patientAccessTheme.palette.primary[500],
    cursor: 'pointer',
  },
  checkbox: {
    color: patientAccessTheme.palette.checkbox?.border1,
    height: '20px',
    width: '20px',
    borderRadius: '2px',
  },
  checkboxChecked: {
    color: `${patientAccessTheme.palette.primary[500]} !important`,
  },
  reportNameGrid: {
    display: 'flex',
    gap: '16px',
    paddingLeft: '16px',
    width: '310px',
  },
  editGrid: {
    width: 'fit-content',
    position: 'absolute',
    top: '16px',
    right: '24px',
  },
});

const ComplianceReportsTable: React.FC<ComplianceReportsProps> = ({
  reportsData,
  handleDownloadAll,
  handleDownloadSelected,
  setSelectedReports,
  showDownloadSelected,
  handleRowDownloadReport,
}) => {
  const { classes } = useStyles();
  const headers = ['Report Name', 'Description', 'Last Generated'];
  const { REPORTS, REPORTS_TABLE_SUBTITLE } = componentConstants.PAGES.REPORTS;
  const reportsTableColumns = [
    {
      id: 'name',
      accessorKey: 'name',
      header: (
        <Typography
          className={classes.tableHeaderText}
          style={{ width: '300px', paddingLeft: '16px' }}
        >
          {headers[0]}
        </Typography>
      ),
      cell: ({ row, getValue }: any) => {
        return (
          <Grid className={classes.reportNameGrid}>
            {row.original?.subRows?.length > 0 && (
              <Grid
                onClick={row.getToggleExpandedHandler?.()}
                style={{ cursor: 'pointer' }}
              >
                <ChevronRight
                  className={classes.chevronRight}
                  style={{
                    transform: row.getIsExpanded?.() ? 'rotate(90deg)' : 'none',
                  }}
                />
              </Grid>
            )}
            {row.depth ? (
              <Grid>
                <Checkbox
                  checked={row.getIsSelected?.() ?? false}
                  indeterminate={row.getIsSomeSelected?.() ?? false}
                  onChange={row.getToggleSelectedHandler?.()}
                  disabled={false}
                  classes={{
                    root: classes.checkbox,
                    checked: classes.checkboxChecked,
                  }}
                />
              </Grid>
            ) : (
              <></>
            )}
            <Grid>
              <Typography
                fontSize="14px"
                fontType={'regular'}
                lineHeight="22.4px"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
              >
                {getValue()}
              </Typography>
            </Grid>
          </Grid>
        );
      },
    },
    {
      id: 'description',
      accessorKey: 'description',
      header: (
        <Typography
          className={classes.tableHeaderText}
          style={{ width: '560px' }}
        >
          {headers[1]}
        </Typography>
      ),
      cell: ({ getValue }: any) => (
        <Grid style={{ width: '560px' }}>
          {getTruncatedText(getValue(), '520px')}
        </Grid>
      ),
    },
    {
      id: 'lastUpdated',
      accessorKey: 'lastUpdated',
      header: (
        <Typography
          className={classes.tableHeaderText}
          style={{ width: '200px' }}
        >
          {headers[2]}
        </Typography>
      ),
      cell: ({ getValue }: any) => (
        <Grid style={{ width: '200px' }}>
          {getTruncatedText(getValue(), '180px')}
        </Grid>
      ),
    },
  ];
  const [rowId, setRowId] = useState<number>();

  const OnHoverRowComponent = (row: any) => {
    return (
      <>
        {row?.subRows?.length ? (
          <></>
        ) : (
          <Grid
            className={classes.editGrid}
            onClick={() => handleRowDownloadReport(row)}
          >
            <Download
              stroke={patientAccessTheme.palette.status.icon}
              height="20px"
              width="20px"
              cursor="pointer"
            />
          </Grid>
        )}
      </>
    );
  };

  const handleMouseLeave = () => {
    setRowId(NaN);
  };

  const handleMouseEnter = (row: any) => {
    setRowId(row?.id);
  };

  return (
    <CardComponent
      title={REPORTS}
      subTitle={REPORTS_TABLE_SUBTITLE}
      width="1178px"
      height="auto"
      body={
        <>
          <Table
            data={reportsData}
            columns={reportsTableColumns}
            onHoverRowData={(row) => OnHoverRowComponent(row)}
            showEditData
            controlledPageIndex={0}
            isManualPagination={true}
            overallGridStyles={classes.tableRoot}
            classNameForTableHeaderRowTop={classes.tableHeaderRow}
            classNameForTableHeader={classes.tableHeaderRow}
            classNameForTableHeaderRowForSelection={clsx(
              classes.tableHeaderRow,
              classes.tableTHSelection,
            )}
            classNameForTableDataRow={classes.tableDataRow}
            classNameForTableDataRowInDepth={clsx(
              classes.tableDataRow,
              classes.subRows,
            )}
            showSelection
            hideSelectionForSubRows
            handleSelectedRows={setSelectedReports}
            isDataEmpty={reportsData?.length === 0}
            noDataText="No Reports Found"
            rowId={rowId}
            handleMouseEnter={handleMouseEnter}
            handleMouseLeave={handleMouseLeave}
          />
        </>
      }
      topRightItem={
        <DownloadButtons
          showDownloadSelected={showDownloadSelected}
          handleDownloadAll={handleDownloadAll}
          handleDowloadSelected={handleDownloadSelected}
        />
      }
    />
  );
};

export default ComplianceReportsTable;
